import { useEffect, useState } from 'react';
import { api, type Apercu, type Supervision } from '../../api/client';
import { Carte, Chargement, EtatVide, MessageErreur, Puce, Statistique } from '../../components/Ui';
import { useEstProduction } from '../../lib/plateforme';
import {
  formaterDate,
  formaterFcfa,
  formaterMontantSigne,
  formaterNombre,
  formaterNumero,
  libelleEcriture,
  operateurDe,
} from '../../lib/format';

/**
 * Tableau de bord — « Chemin du message », d'après le design.
 *
 * Le bandeau du haut suit un message de bout en bout : accepté par l'API, en
 * file, émis vers l'opérateur, accusé reçu. C'est la seule façon de voir d'un
 * coup d'œil où une chaîne se bloque.
 *
 * Les compteurs de file viennent de la supervision, qui n'est ouverte qu'aux
 * administrateurs : un client voit ses propres chiffres, pas ceux des files.
 */
export function TableauDeBord({
  estAdmin,
  surVoirMessage,
  surAllerAuJournal,
}: {
  estAdmin: boolean;
  surVoirMessage: (id: string) => void;
  surAllerAuJournal: () => void;
}) {
  const estProduction = useEstProduction();
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [supervision, setSupervision] = useState<Supervision | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    api.apercu().then(setApercu).catch(setErreur);
    if (estAdmin)
      api.admin
        .supervision()
        .then(setSupervision)
        .catch(() => setSupervision(null));
  }, [estAdmin]);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!apercu) return <Chargement quoi="Chargement du tableau de bord…" />;

  const messages = apercu.recent_messages;
  const enFile = messages.filter((m) => m.status === 'queued').length;
  const soumis = messages.filter((m) =>
    ['submitted', 'sent', 'delivered', 'undelivered', 'expired'].includes(m.status),
  ).length;
  const delivres = messages.filter((m) => m.status === 'delivered').length;
  const nonRemis = messages.filter((m) =>
    ['undelivered', 'expired', 'rejected'].includes(m.status),
  ).length;

  const plafond = supervision?.gateway.global_tps_cap ?? null;
  const enAttenteFiles = supervision?.pending_jobs ?? null;

  return (
    <>
      <div className="carte-relief">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            marginBottom: 18,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.6)',
            }}
          >
            Chemin du message · {messages.length} derniers
          </div>
          <div
            style={{
              marginLeft: 'auto',
              fontSize: 11.5,
              color: 'rgba(255,255,255,.7)',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <span className="point-direct" />
            {estProduction ? 'production' : 'plateforme de test'}
          </div>
        </div>

        <div className="grille auto-150">
          <Statistique intitule="Reçus par l’API" valeur={messages.length} note="acceptés" />
          <Statistique
            intitule="En file"
            valeur={<span style={{ color: 'var(--orange)' }}>{enFile}</span>}
            note="attente d’émission"
          />
          <Statistique intitule="Émis vers opérateur" valeur={soumis} note="submit_sm" />
          <Statistique intitule="Accusés reçus" valeur={delivres} note="DLR remis" />
          <Statistique intitule="Non remis" valeur={nonRemis} note="recrédités" />
        </div>

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid rgba(255,255,255,.14)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11.5,
              color: 'rgba(255,255,255,.7)',
              marginBottom: 8,
              gap: 12,
            }}
          >
            <span>Débit d’émission accordé</span>
            <span className="mono">
              {plafond !== null ? `${plafond} SMS par seconde` : 'plafond contractuel'}
            </span>
          </div>
          <div className="jauge">
            <div style={{ width: enAttenteFiles && enAttenteFiles > 0 ? '100%' : '4%' }} />
          </div>
          <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', marginTop: 9 }}>
            Plafond fixé par le contrat opérateur. Au-delà, les messages patientent en file — ils ne
            sont pas perdus.
            {enAttenteFiles !== null && enAttenteFiles > 0
              ? ` ${formaterNombre(enAttenteFiles)} tâche(s) en attente actuellement.`
              : ''}
          </div>
        </div>
      </div>

      {/* Les indicateurs d'argent n'ont de sens qu'en production : sur la
          plateforme de test rien n'est facturé, et un solde à zéro sous un
          seuil d'alerte fabriquerait une inquiétude sans objet. */}
      {estProduction && (
        <div className="grille auto-210" style={{ marginTop: 16 }}>
          <div className="carte">
            <div className="corps">
              <Statistique
                intitule="Solde du compte"
                valeur={formaterNombre(apercu.balance_xaf)}
                note="FCFA"
                ton="credit"
              />
            </div>
          </div>
          <div className="carte">
            <div className="corps">
              <Statistique
                intitule="Disponible à l’envoi"
                valeur={formaterNombre(apercu.available_xaf)}
                note="solde moins les messages acceptés non encore débités"
              />
            </div>
          </div>
          <div className="carte">
            <div className="corps">
              <Statistique
                intitule="Seuil d’alerte"
                valeur={formaterNombre(apercu.low_balance_threshold_xaf)}
                note={
                  apercu.low_balance ? (
                    <span style={{ color: 'var(--rouge)' }}>Solde sous le seuil.</span>
                  ) : (
                    'FCFA — vous êtes prévenu avant la panne sèche'
                  )
                }
              />
            </div>
          </div>
          <div className="carte">
            <div className="corps">
              <Statistique
                intitule="Coût des derniers envois"
                valeur={formaterNombre(messages.reduce((t, m) => t + m.cost_xaf, 0))}
                note="FCFA hors taxes"
                ton="debit"
              />
            </div>
          </div>
        </div>
      )}

      <div className="rangee" style={{ marginTop: 16 }}>
        <div className="large">
          <Carte
            titre="Derniers messages"
            action={
              <button className="lien" onClick={surAllerAuJournal}>
                Ouvrir le journal complet
              </button>
            }
            sansCorps
          >
            {messages.length === 0 ? (
              <EtatVide titre="Aucun message pour l’instant." />
            ) : (
              <div className="tableau-conteneur">
                <table style={{ minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>Destinataire</th>
                      <th>Expéditeur</th>
                      <th>Opérateur</th>
                      <th className="droite">Coût</th>
                      <th>État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.id}>
                        <td className="mono" style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(m.created_at)}
                        </td>
                        <td className="mono" style={{ fontSize: 12.5 }}>
                          <button className="lien" onClick={() => surVoirMessage(m.id)}>
                            {formaterNumero(m.to)}
                          </button>
                        </td>
                        <td>
                          <span className="etiquette-sid">{m.from}</span>
                        </td>
                        <td style={{ color: 'var(--muet)', fontSize: 13 }}>{operateurDe(m.to)}</td>
                        <td className="nombre">{formaterFcfa(m.cost_xaf)}</td>
                        <td>
                          <Puce valeur={m.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Grand livre" sansCorps>
            {apercu.recent_entries.length === 0 ? (
              <EtatVide titre="Aucune écriture." />
            ) : (
              <div className="tableau-conteneur">
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th className="droite">Montant</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apercu.recent_entries.map((e) => (
                      <tr key={e.id}>
                        <td>{libelleEcriture(e.type)}</td>
                        <td className={`nombre montant ${e.amount_xaf < 0 ? 'debit' : 'credit'}`}>
                          {formaterMontantSigne(e.amount_xaf)}
                        </td>
                        <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(e.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        </div>
      </div>
    </>
  );
}
